import React, {useCallback, useMemo, useState} from "react";
import {AccountingTransactionTypes} from "../../../../../../../../../core/constants/enums";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import classnames from "classnames";
import SummaryActionCard from "../../../../../../../../components/app-specific/summary-cards/summary-action-card";
import {routeFunctions} from "../../../../../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {ReceiptLongRounded} from "@mui/icons-material";
import TransactionReceiptDialog from "../../../../../../../../components/dialogs/transaction-receipt-dialog";


const billInfoSummaryBodyIds = {
    subTotal: 'subTotal',
    discount: 'discount',
    tax: (i) => `tax-${i}`,
    total: 'total',
    transaction: (i) => `transaction-${i}`,
}

const billInfoSummaryActionIds = {
    status: 'status',
    media: "media",
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const CreditInformationViewBillInfoSection = ({
                                                  order,
                                                  loading,
                                                  customerId,
                                              }) => {
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const operatorAccessLevels = useAccessLevels();

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    const billInfoSummaryBody = useMemo(() => {
        const res = [];
        const transactions = order?.appliedTransactions?.filter(e => ![AccountingTransactionTypes.userOrder, AccountingTransactionTypes.invoice].includes(e.transactionTypeName)) ?? [];
        res.push({
            id: billInfoSummaryBodyIds.subTotal,
            title: 'Sub total',
            value: order?.subTotal,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        })
        let taxI = 0;
        for (const tax of order?.taxes ?? []) {
            res.push({
                id: billInfoSummaryBodyIds.tax(taxI++),
                title: <span className={classnames({'line-through': tax?.exemptNo?.length})}>
                    {tax.shortName ?? '--'} ({tax?.rate ?? '--'}%)
                </span>,
                value: tax?.exemptNo?.length ? `exempt: ${tax?.exemptNo}` : formatMoney(tax.total),
                loadingValue: "loading...",
                action: {show: false},
            })
        }
        res.push({
            id: billInfoSummaryBodyIds.total,
            title: 'Total',
            value: order?.total,
            loadingValue: "loading...",
            format: formatMoney,
            action: {show: false},
        });
        let transactionI = 0;
        for (const transaction of transactions) {
            const showReceipt = transaction?.transactionTypeName === AccountingTransactionTypes.payment && transaction?.receiptId && operatorAccessLevels.packages.sales.paymentReceipts;
            res.push({
                id: billInfoSummaryBodyIds.transaction(transaction?.id ?? --transactionI),
                titleSuffix:
                    showReceipt
                        ? <IconButton
                            color={'paper.primary'}
                            className={'ml-1'}
                            onPressed={() => openReceiptDialog(transaction)}
                        >
                            <ReceiptLongRounded className={'icon normal'}/>
                        </IconButton>
                        : null,
                title: transaction?.transactionTypeDescription,
                value: -(transaction?.amount ?? 0),
                loadingValue: "loading...",
                format: formatMoney,
                action: {show: false},
            })
        }
        return res;
    }, [order?.appliedTransactions, order?.subTotal, order?.total, order?.taxes, operatorAccessLevels.packages.sales.paymentReceipts, openReceiptDialog])

    const billInfoSummaryActions = useMemo(() => [
        {
            id: billInfoSummaryActionIds.media,
            value: 'View Media',
            submittingValue: 'Submitting...',
            show: operatorAccessLevels.packages.warrantyAndReturn.viewDetails && !!order && !!order?.mediaId,
            variant: 'contained',
            props: {
                component: Link,
                to: routeFunctions.warrantyAndReturn.single(order?.mediaId),
                target: '_blank',
            },
        },
        {
            id: billInfoSummaryActionIds.status,
            value: order?.status?.name ?? 'status unknown',
            submittingValue: 'Submitting...',
            show: !!order && !!order.status?.name,
            disabled: true,
            variant: 'text',
        },
    ], [operatorAccessLevels.packages.warrantyAndReturn.viewDetails, order])

    const billInfoSummary = useMemo(() => ({
        header: {
            title: 'Balance',
            value: order?.balance,
            loadingValue: "Loading...",
            format: formatMoney,
        },
        body: billInfoSummaryBody,
        actions: billInfoSummaryActions,
    }), [billInfoSummaryBody, billInfoSummaryActions, order?.balance])


    return (
        <>
            <SummaryActionCard
                className={'auto-height'}
                header={billInfoSummary.header}
                body={billInfoSummary.body}
                actions={billInfoSummary.actions}
                loadingCount={billInfoSummary.body.length}
                loading={loading}
            />
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}


export default CreditInformationViewBillInfoSection;
